import { useCallback, useState } from 'react';

import type { S3Error } from './types';

export interface OperationProgress {
  isInProgress: boolean;
  progress: number;
  error: S3Error | null;
  isCancelled: boolean;
  start: () => void;
  updateProgress: (progress: number) => void;
  setError: (error: S3Error) => void;
  cancel: () => void;
  reset: () => void;
}

export const useOperationProgress = (): OperationProgress => {
  const [isInProgress, setIsInProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<S3Error | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);

  const start = useCallback(() => {
    setIsInProgress(true);
    setProgress(0);
    setError(null);
    setIsCancelled(false);
  }, []);

  const updateProgress = useCallback((newProgress: number) => {
    setProgress(newProgress);
  }, []);

  const handleSetError = useCallback((err: S3Error) => {
    setError(err);
  }, []);

  const cancel = useCallback(() => {
    setIsCancelled(true);
    setIsInProgress(false);
  }, []);

  const reset = useCallback(() => {
    setIsInProgress(false);
    setProgress(0);
    setIsCancelled(false);
  }, []);

  return {
    isInProgress,
    progress,
    error,
    isCancelled,
    start,
    updateProgress,
    setError: handleSetError,
    cancel,
    reset,
  };
};

export const createMultiFileProgressTracker = (fileCount: number, onProgressUpdate: (progress: number) => void) => {
  const fileProgress = new Map<number, number>();

  const notifyOverallProgress = () => {
    const totalProgress = Array.from(fileProgress.values()).reduce((sum, progress) => sum + progress, 0);
    const averageProgress = Math.round(totalProgress / fileCount);
    onProgressUpdate(averageProgress);
  };

  const updateFileProgress = (fileIndex: number, progress: number) => {
    fileProgress.set(fileIndex, progress);
    notifyOverallProgress();
  };

  const createFileProgressCallback = (fileIndex: number) => {
    return (progress: number) => {
      updateFileProgress(fileIndex, progress);
    };
  };

  return {
    updateFileProgress,
    createFileProgressCallback,
  };
};
